import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TabPanel } from '@mui/lab';
import { StatisticsChart } from './StatisticsChart';
import { JournalsTable } from './JournalsTable';
import { WidgetOriginsTable } from './WidgetOriginsTable';
import { TabConfig } from '../config/tabConfig';
import { Order, JournalByDate } from '../Statistics.types';

interface StatisticsTabPanelProps {
  tab: TabConfig;
  index: number;
  value: string;
  data: any;
  isLoading: boolean;
  isError?: boolean;
  isMobile: boolean;
  sortedJournals?: any[];
  order?: Order;
  orderBy?: keyof JournalByDate;
  onSort?: (property: keyof JournalByDate) => void;
}

export const StatisticsTabPanel: React.FC<StatisticsTabPanelProps> = ({
  tab,
  index,
  value,
  data,
  isLoading,
  isError = false,
  isMobile,
  sortedJournals,
  order,
  orderBy,
  onSort,
}) => {
  const { t } = useTranslation();

  return (
    <TabPanel
      value={value}
      sx={{
        p: 2.5,
        overflow: 'hidden',
        maxWidth: '100%',
        minHeight: 500,
      }}
      id={`statistics-tabpanel-${index}`}
      aria-labelledby={`statistics-tab-${index}`}
      tabIndex={0}
    >
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          p={3}
          role="status"
          aria-live="polite"
        >
          <CircularProgress aria-label={`Loading ${t(tab.labelKey)} data`} />
          <span
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              borderWidth: 0,
            }}
          >
            Loading {t(tab.labelKey)} data
          </span>
        </Box>
      ) : (
        <Box>
          {tab.type === 'chart' ? (
            <StatisticsChart data={data} tabKey={tab.key} isMobile={isMobile} />
          ) : tab.type === 'table' &&
            tab.key === 'journals' &&
            sortedJournals &&
            onSort ? (
            <JournalsTable
              sortedJournals={sortedJournals}
              onSort={onSort}
              order={order!}
              orderBy={orderBy!}
            />
          ) : tab.type === 'table' && tab.key === 'widgetOrigins' ? (
            <WidgetOriginsTable rows={data?.rows || []} isError={isError} />
          ) : null}
        </Box>
      )}
    </TabPanel>
  );
};
